import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    Flex,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalOverlay,
    Spinner,
    Text
} from '@chakra-ui/react';
import BraintreeWebClient, { Client } from 'braintree-web/client';
import BraintreeDataCollector from 'braintree-web/data-collector';
import Braintree3DSecure, {
    ThreeDSecure,
    ThreeDSecureVerifyPayload
} from 'braintree-web/three-d-secure';
import { useTranslate } from 'ts/common/hooks';
import { useBootstrapData } from 'client_react/bootstrap';

interface CreditCardDetails {
    number: string;
    cvv: string;
    expirationDate: string; // MM/YYYY format
    billingAddress: {
        postalCode: string;
    };
    deviceData?: string;
}

export interface BraintreeCreditCardInfo {
    lastFour?: string;
    lastTwo?: string;
    cardType?: string;
    bin?: string;
    expirationMonth?: string;
    expirationYear?: string;
}

interface BraintreeTokenizedResponse {
    creditCards: Array<{
        nonce: string;
        details: {
            bin: string;
            lastFour?: string;
            lastTwo?: string;
            cardType?: string;
        };
    }>;
}

interface BraintreeError {
    code?: string;
    message?: string;
}

interface Braintree3DSChallengeProps {
    isOpen: boolean;
    creditCardDetails: CreditCardDetails;
    chargeAmount: number;
    onComplete: (result: {
        paymentMethodNonce: string;
        creditCard: BraintreeCreditCardInfo;
    }) => void;
    onClose: () => void;
}

const VERIFIED_STATUS_SUCCESS = 'authenticate_successful';
const VERIFIED_STATUS_UNAVAILABLE = 'authentication_unavailable';

const Braintree3DSChallenge: React.FC<Braintree3DSChallengeProps> = ({
    isOpen,
    creditCardDetails,
    chargeAmount,
    onComplete,
    onClose
}) => {
    const t = useTranslate('clientInvoice');
    const { braintreeToken } = useBootstrapData();
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);
    const challengeContainerRef = useRef<HTMLDivElement | null>(null);

    const createBraintreeClient = useCallback(async () => {
        if (!braintreeToken) {
            throw new Error('Braintree token not available');
        }

        return BraintreeWebClient.create({
            authorization: braintreeToken
        });
    }, [braintreeToken]);

    const createDataCollector = useCallback(async (standardClient: Client) => {
        try {
            const dataCollector = await BraintreeDataCollector.create({
                client: standardClient,
                kount: true
            });
            return dataCollector.deviceData;
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Failed to create data collector:', err);
            // Return null if device data collection fails, payment can still proceed
            return null;
        }
    }, []);

    const create3DSecureClient = useCallback(async (standardClient: Client) => {
        return Braintree3DSecure.create({
            client: standardClient,
            version: '2-inline-iframe'
        }).then((secureClient: ThreeDSecure) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (secureClient as any).on(
                'authentication-iframe-available',
                (event: { element: HTMLElement }, next: () => void) => {
                    const { element } = event;

                    // Insert iframe immediately into the DOM before calling next()
                    // This matches the AngularJS implementation
                    if (challengeContainerRef.current) {
                        // Clear any existing iframes before appending
                        challengeContainerRef.current.innerHTML = '';
                        challengeContainerRef.current.appendChild(element);
                        setIsLoading(false);
                    }

                    next();
                }
            );

            return secureClient;
        });
    }, []);

    const tokenizeCreditCard = useCallback(
        async (client: Client, creditCard: CreditCardDetails) => {
            return new Promise<BraintreeTokenizedResponse>((resolve, reject) => {
                client.request(
                    {
                        endpoint: 'payment_methods/credit_cards',
                        method: 'post',
                        data: {
                            creditCard
                        }
                    },
                    (err, response) => {
                        if (err) {
                            reject(err);
                        } else {
                            resolve(response as BraintreeTokenizedResponse);
                        }
                    }
                );
            });
        },
        []
    );

    const parsePaymentMethodNonce = useCallback(
        (tokenizedCreditCardData: BraintreeTokenizedResponse) => {
            const { creditCards } = tokenizedCreditCardData;
            const [{ nonce }] = creditCards;
            return nonce;
        },
        []
    );

    const handleVerificationFlow = useCallback(
        async (secureClient: ThreeDSecure, paymentMethodNonce: string, bin: string) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const response: ThreeDSecureVerifyPayload = await (secureClient as any).verifyCard({
                nonce: paymentMethodNonce,
                bin: bin,
                amount: chargeAmount,
                onLookupComplete: (_: unknown, next: () => void) => {
                    next();
                }
            });

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const { status: verifiedStatus } = response.threeDSecureInfo as any;
            const isVerified = [VERIFIED_STATUS_SUCCESS, VERIFIED_STATUS_UNAVAILABLE].includes(
                verifiedStatus
            );

            return {
                creditCard: response.details,
                paymentMethodNonce: isVerified ? response.nonce : null,
                verified: isVerified,
                verifiedStatus
            };
        },
        [chargeAmount]
    );

    const getBraintreeVerificationCodeMessage = useCallback(
        (code: string): string => {
            const verificationMessages: Record<string, string> = {
                authenticate_attempt_successful: t('braintree3DS.authenticateAttemptSuccessful'),
                authenticate_successful: t('braintree3DS.authenticateSuccessful'),
                authentication_unavailable: t('braintree3DS.authenticationUnavailable'),
                authentication_failed: t('braintree3DS.authenticationFailed'),
                authentication_cancelled: t('braintree3DS.authenticationCancelled'),
                authentication_error: t('braintree3DS.authenticationError')
            };

            return verificationMessages[code] || t('braintree3DS.verificationFailed');
        },
        [t]
    );

    const showError = useCallback(
        (message: string) => {
            if (challengeContainerRef.current) {
                challengeContainerRef.current.innerHTML = '';
            }

            setErrorMessage(message || t('braintree3DS.verificationFailed'));
            setIsLoading(false);
        },
        [t]
    );

    const perform3DSVerification = useCallback(async () => {
        if (!isOpen || isLoading) {
            return;
        }

        // Reset error state and start loading
        setIsLoading(true);
        setErrorMessage(null);

        try {
            // Create Braintree client
            const standardClient = await createBraintreeClient();

            // Collect device data
            const deviceData = await createDataCollector(standardClient);

            // Include device data in credit card details
            const creditCardWithDeviceData = {
                ...creditCardDetails,
                ...(deviceData && { deviceData })
            };

            // Tokenize credit card with device data
            const tokenizedResponse = await tokenizeCreditCard(
                standardClient,
                creditCardWithDeviceData
            );

            const bin = tokenizedResponse.creditCards[0].details.bin;
            const paymentMethodNonce = parsePaymentMethodNonce(tokenizedResponse);

            // Create 3DS client
            const secureClient = await create3DSecureClient(standardClient);

            // Perform verification
            const result = await handleVerificationFlow(secureClient, paymentMethodNonce, bin);

            if (!result.verified || !result.paymentMethodNonce) {
                const verificationMessage = result.verifiedStatus
                    ? getBraintreeVerificationCodeMessage(result.verifiedStatus)
                    : t('braintree3DS.verificationFailed');
                showError(verificationMessage);
                return;
            }

            onComplete({
                paymentMethodNonce: result.paymentMethodNonce,
                creditCard: result.creditCard
            });
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('3DS verification error:', err);
            const braintreeError = err as BraintreeError;
            const errorMessage = braintreeError?.code
                ? getBraintreeVerificationCodeMessage(braintreeError.code)
                : t('braintree3DS.verificationFailed');
            showError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    }, [
        isOpen,
        isLoading,
        creditCardDetails,
        createBraintreeClient,
        createDataCollector,
        tokenizeCreditCard,
        parsePaymentMethodNonce,
        create3DSecureClient,
        handleVerificationFlow,
        getBraintreeVerificationCodeMessage,
        showError,
        onComplete,
        t
    ]);

    useEffect(() => {
        if (isOpen) {
            perform3DSVerification();
        } else {
            // Reset state when modal closes
            setIsLoading(false);

            if (challengeContainerRef.current) {
                challengeContainerRef.current.innerHTML = '';
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    return (
        <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false} closeOnEsc={false}>
            <ModalOverlay />
            <ModalContent
                maxW={{ base: '100%', lg: '438px' }}
                height={{ base: '100%', lg: '750px' }}
                maxHeight="100vh"
            >
                <ModalCloseButton border="none" />
                <ModalBody px={6}>
                    {errorMessage ? (
                        <Box
                            textAlign="center"
                            height={{ base: '100%', lg: '750px' }}
                            maxHeight="100vh"
                            width="100%"
                        >
                            <Box
                                bg="red.50"
                                borderColor="red.500"
                                borderWidth="1px"
                                borderRadius="md"
                                p={4}
                                mb={4}
                            >
                                <Text color="red.700" fontWeight="semibold">
                                    {errorMessage}
                                </Text>
                            </Box>
                            <Button colorScheme="blue" onClick={onClose}>
                                {t('braintree3DS.close')}
                            </Button>
                        </Box>
                    ) : (
                        <>
                            {isLoading && (
                                <Flex
                                    alignItems="center"
                                    justifyContent="center"
                                    height={{ base: '100%', lg: '750px' }}
                                    maxHeight="100vh"
                                    width="100%"
                                >
                                    <Spinner size="xl" />
                                </Flex>
                            )}
                            <Box
                                ref={challengeContainerRef}
                                height={{ base: '100%', lg: '750px' }}
                                maxHeight="100vh"
                                width="100%"
                                display={isLoading ? 'none' : 'flex'}
                                sx={{
                                    '> div': {
                                        height: '100%',
                                        width: '100%'
                                    },
                                    iframe: {
                                        height: '100%',
                                        width: '100%'
                                    }
                                }}
                            />
                        </>
                    )}
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default Braintree3DSChallenge;
